import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { init, warmup, startPayment as ttpStartPayment, diagnose, linkAccount } from '@alterrego/module-tap-to-pay';
import type { PaymentResult, DiagnoseResult } from '@alterrego/module-tap-to-pay';
import { AmountKeypad } from '../../components/AmountKeypad';
import { DebugOverlay } from '../../components/DebugOverlay';
import { apiFetch, BACKEND_URL } from '../../utils/api';
import './Staff.css';
import './StaffPayment.css';

type PayMode = 'card' | 'link';
type Screen = 'amount' | 'reader' | 'processing' | 'result' | 'link-details' | 'link-result';

export function StaffPayment() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<PayMode>('card');
  const [screen, setScreen] = useState<Screen>('amount');
  const [rawAmount, setRawAmount] = useState('');
  const [result, setResult] = useState<PaymentResult | null>(null);
  const [diagnoseResult, setDiagnoseResult] = useState<DiagnoseResult | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [debugLines, setDebugLines] = useState<string[]>([]);

  // Link fields
  const [description, setDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkRef, setLinkRef] = useState('');
  const [sending, setSending] = useState(false);

  const amount = parseInt(rawAmount || '0', 10);
  const amountDisplay = (cents: number) => (cents / 100).toFixed(2).replace('.', ',') + ' EUR';

  const addDebug = (msg: string) => {
    const ts = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const line = `[${ts}] ${msg}`;
    console.log('[TTP DEBUG]', line);
    setDebugLines(prev => [...prev.slice(-6), line]);
  };

  // Pre-warm Tap to Pay reader when payment screen mounts
  useEffect(() => {
    addDebug('étape 1/3 — init() →');
    init({
      environment: 'test',
      merchantAccount: 'Alterrego_Sauf_Imprevu_TEST',
      backendUrl: BACKEND_URL,
    }).then(() => {
      addDebug('étape 2/3 — init OK → warmup()');
      return warmup();
    }).then(() => {
      addDebug('étape 3/3 — warmup OK, SDK prêt');
    }).catch((err: any) => {
      const msg = err?.message ?? String(err);
      addDebug(`INIT FAILED: ${msg}`);
    });
  }, []);

  // ── Card flow ──
  const startPayment = async () => {
    if (amount <= 0) return;
    setScreen('reader');
    addDebug(`startPayment(${amountDisplay(amount)}) ref=SI_${Date.now()}`);
    try {
      const ref = `SI_${Date.now()}`;
      addDebug('→ appel TapToPayPlugin.startPayment…');
      const res = await ttpStartPayment({
        amount,
        currency: 'EUR',
        reference: ref,
      });
      addDebug(`résultat: status=${res.status} psp=${res.pspReference ?? 'n/a'} err=${res.errorMessage ?? 'none'}`);
      setResult(res);
      setScreen('result');
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      const stack = err?.stack ? ` | stack: ${err.stack.split('\n')[1]?.trim()}` : '';
      addDebug(`THROW: ${msg}${stack}`);
      setResult({
        status: 'error',
        reference: `SI_${Date.now()}`,
        amount,
        currency: 'EUR',
        errorMessage: msg,
        timestamp: new Date().toISOString(),
      });
      setScreen('result');
    }
  };

  // ── Link flow ──
  const generateLink = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('alterrego_jwt');
      addDebug(`generateLink — jwt ${token ? 'présent' : 'ABSENT'}`);

      const res = await apiFetch('/pay-by-link/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          description: description || 'Paiement Sauf Imprevu',
          shopperName: clientName || undefined,
          shopperEmail: clientEmail || undefined,
          expiresIn: '7d',
        }),
      });
      if (!res.ok) {
        const rawBody = await res.text().catch(() => '(empty)');
        addDebug(`POST /pay-by-link/create → ${res.status} — ${rawBody.slice(0, 120)}`);
        alert(`Erreur ${res.status}: ${rawBody.slice(0, 200)}`);
        return;
      }
      const data = await res.json();
      setLinkUrl(data.url);
      setLinkRef(data.reference);
      setScreen('link-result');
    } catch (e: any) {
      addDebug(`generateLink THROW: ${e?.message}`);
      alert('Erreur: ' + e.message);
    } finally { setSending(false); }
  };

  const shareLink = async () => {
    if (navigator.share) {
      await navigator.share({ title: 'Paiement Sauf Imprevu', text: `Paiement de ${amountDisplay(amount)}`, url: linkUrl }).catch(() => {});
    } else { await navigator.clipboard.writeText(linkUrl); alert('Lien copie !'); }
  };
  const sendSMS = () => { window.open(`sms:${clientPhone}?body=${encodeURIComponent(`Paiement de ${amountDisplay(amount)} : ${linkUrl}`)}`); };
  const sendEmail = () => {
    const body = `Bonjour${clientName ? ' ' + clientName : ''},\n\nVoici votre lien de paiement de ${amountDisplay(amount)}${description ? ' pour ' + description : ''} :\n\n${linkUrl}\n\nA bientot !\nSauf Imprevu`;
    window.open(`mailto:${clientEmail}?subject=${encodeURIComponent(`Paiement — ${amountDisplay(amount)}`)}&body=${encodeURIComponent(body)}`);
  };

  const runLinkAccount = async () => {
    setLinking(true);
    addDebug('linkAccount() →');
    try {
      const r = await linkAccount();
      addDebug(`linkAccount → success=${r.success}${r.error ? ' err=' + r.error : ''}`);
    } catch (err: any) {
      addDebug(`linkAccount THROW: ${err?.message ?? String(err)}`);
    } finally {
      setLinking(false);
    }
  };

  const runDiagnose = async () => {
    setDiagnosing(true);
    setDiagnoseResult(null);
    addDebug('diagnose() →');
    try {
      const r = await diagnose();
      addDebug(`diagnose OK — ttp=${r.tapToPayInterfaceResult} linked=${r.accountLinked} warmup=${r.warmUpResult}`);
      setDiagnoseResult(r);
    } catch (err: any) {
      addDebug(`diagnose THROW: ${err?.message ?? String(err)}`);
    } finally {
      setDiagnosing(false);
    }
  };

  const reset = () => {
    setScreen('amount');
    setRawAmount('');
    setResult(null);
    setDiagnoseResult(null);
    setDescription(''); setClientName(''); setClientPhone(''); setClientEmail('');
    setLinkUrl(''); setLinkRef('');
  };

  const handleValidate = () => {
    if (amount <= 0) return;
    if (mode === 'card') { startPayment(); }
    else setScreen('link-details');
  };

  // ── AMOUNT (shared) ──
  if (screen === 'amount') {
    return (
      <div className="ttp-page">
        <div className="staff-header">
          <button className="staff-back" onClick={() => navigate('/staff/dashboard')}>‹</button>
          <h1>Paiement</h1>
          <div style={{ width: 36 }} />
        </div>
        <AmountKeypad
          amount={rawAmount}
          onDigit={(d) => rawAmount.length < 8 && setRawAmount(rawAmount + d)}
          onDot={() => {}}
          onDelete={() => setRawAmount(rawAmount.slice(0, -1))}
          onClear={() => setRawAmount('')}
          onValidate={handleValidate}
          validateDisabled={amount <= 0}
          label="Montant"
          methodToggle={
            <div className="pay-toggle">
              <button className={`pay-toggle-btn ${mode === 'card' ? 'active' : ''}`} onClick={() => setMode('card')}>💳 Carte bancaire</button>
              <button className={`pay-toggle-btn ${mode === 'link' ? 'active' : ''}`} onClick={() => setMode('link')}>🔗 Lien</button>
            </div>
          }
        />
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={runDiagnose}
              disabled={diagnosing}
              style={{
                flex: 1, padding: '10px', borderRadius: 8,
                background: '#0a1a2a', border: '1px solid #1e4d7a',
                color: '#5599cc', fontSize: 13, cursor: diagnosing ? 'default' : 'pointer',
              }}
            >
              {diagnosing ? 'Diagnostic...' : 'Diagnostic Tap to Pay'}
            </button>
            <button
              onClick={runLinkAccount}
              disabled={linking}
              style={{
                flex: 1, padding: '10px', borderRadius: 8,
                background: '#0a1a2a', border: '1px solid #4d7a1e',
                color: '#77cc44', fontSize: 13, cursor: linking ? 'default' : 'pointer',
              }}
            >
              {linking ? 'Liaison...' : 'Link Apple Account'}
            </button>
          </div>
          {diagnoseResult && (
            <div style={{
              marginTop: 8, background: '#0a1a0a', border: '1px solid #1e4d1e',
              borderRadius: 8, padding: '10px 12px', fontFamily: 'monospace',
              fontSize: 11, color: '#88cc88', lineHeight: 1.6,
            }}>
              <div>iOS {diagnoseResult.iosVersion} ({diagnoseResult.iosVersionOk ? 'OK' : 'trop ancien'})</div>
              <div>Modèle : {diagnoseResult.model} | iPhone : {diagnoseResult.isIphone ? 'oui' : 'non'}</div>
              <div>Passcode : {diagnoseResult.passcodeEnabled ? 'oui' : 'NON'} | Biométrie : {diagnoseResult.biometryEnabled ? 'oui' : 'non'}</div>
              <div>TTP interface : <strong style={{ color: diagnoseResult.tapToPayInterfaceResult === 'ok' ? '#44ee44' : '#ee4444' }}>{diagnoseResult.tapToPayInterfaceResult}</strong></div>
              <div>Account linked : {diagnoseResult.accountLinked ? 'oui' : 'non'}</div>
              <div>WarmUp : {diagnoseResult.warmUpResult}</div>
              <div>SDK : {diagnoseResult.sdkVersion}</div>
              {diagnoseResult.errorReason ? <div style={{ color: '#ee8844' }}>Raison : {diagnoseResult.errorReason}</div> : null}
            </div>
          )}
        </div>
        <DebugOverlay lines={debugLines} label="TTP" color="#00ff88" />
      </div>
    );
  }

  // ── CARD: Reader ──
  if (screen === 'reader') {
    return (
      <div className="ttp-page ttp-center">
        <div className="ttp-reader-amount">{amountDisplay(amount)}</div>
        <div className="ttp-reader-animation">
          <div className="ttp-wave" /><div className="ttp-wave delay1" /><div className="ttp-wave delay2" />
          <div className="ttp-card-icon">💳</div>
        </div>
        <p className="ttp-reader-text">Presentez la carte ou le telephone</p>
        <p className="ttp-reader-sub">Contactless, Apple Pay, Google Pay</p>
        <button className="ttp-cancel" onClick={reset}>Annuler</button>
        <DebugOverlay lines={debugLines} label="TTP" color="#00ff88" />
      </div>
    );
  }

  // ── CARD: Processing ──
  if (screen === 'processing') {
    return (
      <div className="ttp-page ttp-center">
        <div className="ttp-reader-amount">{amountDisplay(amount)}</div>
        <div className="ttp-spinner" />
        <p className="ttp-reader-text">Traitement en cours...</p>
        <DebugOverlay lines={debugLines} label="TTP" color="#00ff88" />
      </div>
    );
  }

  // ── CARD: Result ──
  if (screen === 'result') {
    const isError = result?.status !== 'approved' && result?.status !== 'declined';
    return (
      <div className="ttp-page ttp-center">
        <div className={`ttp-result-icon ${result?.status === 'approved' ? 'success' : 'error'}`}>
          {result?.status === 'approved' ? '✓' : '✗'}
        </div>
        <div className="ttp-result-amount">{amountDisplay(result?.amount ?? 0)}</div>
        <p className="ttp-result-status">
          {result?.status === 'approved' ? 'Paiement approuve' : result?.status === 'declined' ? 'Paiement refuse' : `Erreur (${result?.status ?? 'unknown'})`}
        </p>
        {/* Bloc d'erreur détaillé — visible directement dans l'app */}
        {isError && result?.errorMessage && (
          <div style={{
            background: '#1a0000', border: '1px solid #cc3333', borderRadius: 8,
            padding: '10px 14px', margin: '12px 16px 0', textAlign: 'left',
            fontFamily: 'monospace', fontSize: 12, color: '#ff9999', wordBreak: 'break-all',
          }}>
            {result.errorMessage}
          </div>
        )}
        {result?.status === 'approved' && (
          <div className="ttp-result-details">
            <div><span>Carte</span><strong>{result.cardScheme?.toUpperCase()} ****{result.cardLastFour}</strong></div>
            <div><span>Ref.</span><strong>{result.pspReference}</strong></div>
          </div>
        )}
        <div className="ttp-result-actions">
          <button className="ttp-receipt-btn">Envoyer le recu</button>
          <button className="ttp-new-btn" onClick={reset}>Nouvel encaissement</button>
        </div>
        <DebugOverlay lines={debugLines} label="TTP" color="#00ff88" />
      </div>
    );
  }

  // ── LINK: Details ──
  if (screen === 'link-details') {
    return (
      <div className="ttp-page">
        <div className="staff-header">
          <button className="staff-back" onClick={() => setScreen('amount')}>‹</button>
          <h1>Details du lien</h1>
          <div style={{ width: 36 }} />
        </div>
        <div className="pbl-details-amount">{amountDisplay(amount)}</div>
        <div className="pbl-form">
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optionnel)" />
          <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Nom du client (optionnel)" />
          <input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="Telephone (optionnel)" type="tel" />
          <input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="Email (optionnel)" type="email" />
        </div>
        <div className="pbl-footer">
          <button className="pbl-cta" onClick={generateLink} disabled={sending}>
            {sending ? 'Generation...' : 'Creer le lien de paiement'}
          </button>
        </div>
        <DebugOverlay lines={debugLines} label="TTP" color="#00ff88" />
      </div>
    );
  }

  // ── LINK: Result ──
  return (
    <div className="ttp-page ttp-center">
      <div className="ttp-result-icon success">✓</div>
      <h2 className="pbl-result-title">Lien de paiement cree</h2>
      <div className="ttp-result-amount">{amountDisplay(amount)}</div>
      {description && <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>{description}</p>}
      <div className="pbl-link-box"><span className="pbl-link-url">{linkUrl}</span></div>
      <p className="pbl-link-ref">Ref. {linkRef} · Expire dans 7 jours</p>
      <div className="pbl-share-actions">
        <button className="pbl-share-btn" onClick={shareLink}>🔗 Copier / Partager</button>
        {clientPhone && <button className="pbl-share-btn" onClick={sendSMS}>💬 SMS</button>}
        {clientEmail && <button className="pbl-share-btn" onClick={sendEmail}>✉️ Email</button>}
      </div>
      <button className="pbl-new" onClick={reset}>Nouveau paiement</button>
      <DebugOverlay lines={debugLines} label="TTP" color="#00ff88" />
    </div>
  );
}
